import { Link, useParams } from 'react-router-dom'
import type { MemoirSummary } from '../../../../types'
import { useMemoirs } from '../lib/hooks'
import AlbumList from './album-list'

/**
 * `/memoir/:memoirId` (SOL-204): the album workspace, scoped to one Memoir.
 *
 * Resolves the id against the user's OWN Memoirs, so a Memoir that is not
 * theirs — or a stale bookmark to one that is gone — reads as not found rather
 * than as an empty workspace they could create albums in. The server would
 * refuse those uploads anyway (403 / 404); this just says so first.
 *
 * Keyed on the id so moving between Memoirs resets the workspace's folder and
 * selection state instead of carrying it across.
 */
export default function MemoirWorkspace({ currentUserId }: { currentUserId: string }): JSX.Element {
  const { memoirId } = useParams<{ memoirId: string }>()
  const { memoirs, invitations, loading, error, refresh } = useMemoirs()
  const memoir = memoirs.find((m) => m.uuid === memoirId)
  // An accepted invitee may look, read-only, until the Memoir is handed over.
  const invitation = memoir ? undefined : invitations.find((i) => i.uuid === memoirId && i.acceptedAt)

  if (!memoir && !invitation && loading) {
    return <div className="album-list-loading"><p>Opening your Memoir…</p></div>
  }

  if (invitation) {
    // SYNTHETIC. Only uuid and name are real. createdAt/paidAt/paidUntil are
    // the invitation date as filler, and writable/status are fixed so nothing
    // offers uploads. Never display those dates or treat them as payment facts;
    // viewOnly keeps the payment, renewal and handover panels off this scope.
    const viewing: MemoirSummary = {
      uuid: invitation.uuid,
      name: invitation.name,
      createdAt: invitation.invitedAt,
      paidAt: invitation.invitedAt,
      paidUntil: invitation.invitedAt,
      writable: false,
      status: 'active',
    }
    return <AlbumList key={viewing.uuid} currentUserId={currentUserId} scope={{ kind: 'memoir', memoir: viewing, viewOnly: true }} />
  }

  if (!memoir) {
    return (
      <div className="memoirs-home memoirs-home--error">
        <p>{error ? "We couldn't load this Memoir." : "This Memoir isn't in your account."}</p>
        {error && <button className="memoirs-home__retry" onClick={() => void refresh()}>Try again</button>}
        <Link to="/">Back to your Memoirs</Link>
      </div>
    )
  }

  return (
    <AlbumList
      key={memoir.uuid}
      currentUserId={currentUserId}
      scope={{ kind: 'memoir', memoir, onMemoirChanged: () => void refresh(true) }}
    />
  )
}
